import React from "react";
import type { ApolloClient } from "@apollo/client";
import "react-datepicker/dist/react-datepicker.css";
import { Row, Col, Form } from "react-bootstrap";
import { Formik } from "formik";
import { connect, type ConnectedProps } from "react-redux";
import { userService } from "services/userService";
import { filter, find, isBoolean, some } from "lodash";
import {
  addRolloverAccountDisclosure,
  getUserState,
  updateIraAccountDisclosure,
} from "actions/userActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { accountConstants, userConstants } from "actions/types";
import { getAllAccounts } from "actions/accountActions";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { ScrollToFieldError } from "utils/form";
import {
  isOnboardingSubsequentAccountSelector,
  isRolloverSelector,
} from "store/selectors/user";
import { scrollToTop } from "utils/dom";
import { FiMinus, FiPlus } from "react-icons/fi";
import { DISCLOSURES } from "statics/onboardingSteps";
import {
  PRINTABLE_ASCII_MESSAGE,
  testPrintableAsciiCharacters,
  ALPHANUMERIC_SPACES_MESSAGE,
  testAlphanumericSpaces,
} from "utils/fieldValidators";

import Button from "components/Button";
import FileUploader from "components/FileUploader";
import Alert from "components/Alert";
import TypeUtils from "utils/typeUtils";
import ProgressBar from "components/ProgressBar";
import CircularProgressBar from "components/CircularProgressBar";
import CancelRolloverModal from "../../dashboards/individualDashboard/dashboard/CancelRolloverModal";
import type { RootState } from "types/state";

import * as yup from "yup";
const schema = yup.object({
  noneApply: yup.boolean().required().label("None Apply"),
  controlPerson: yup.boolean().label("Control Person").required(),
  isCustodianEmployee: yup.boolean().label("Custodian Employee").required(),
  stockSymbols: yup
    .string()
    .when("controlPerson", {
      is: true,
      then: (s) => s.required().min(2).max(4),
      otherwise: (s) => s,
    })
    .test("stock-symbol", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
    .label("Stock Symbol"),
  exchangeOrFINRA: yup
    .boolean()
    .label("Affilated Exchange Or FINRA")
    .required(),
  firmName: yup
    .string()
    .when("exchangeOrFINRA", {
      is: true,
      then: (s) =>
        s
          .required()
          .test(
            "firm-name",
            PRINTABLE_ASCII_MESSAGE,
            testPrintableAsciiCharacters
          )
          .test(
            "firm-name",
            ALPHANUMERIC_SPACES_MESSAGE,
            testAlphanumericSpaces
          )
          .min(2)
          .max(50),
      otherwise: (s) =>
        s
          .test(
            "firm-name",
            PRINTABLE_ASCII_MESSAGE,
            testPrintableAsciiCharacters
          )
          .test(
            "firm-name",
            ALPHANUMERIC_SPACES_MESSAGE,
            testAlphanumericSpaces
          ),
    })
    .label("Firm Name"),
  politicallyExposed: yup.boolean().label("Politically Exposed").required(),
  organizationName: yup
    .string()
    .when("politicallyExposed", {
      is: true,
      then: (s) =>
        s
          .required()
          .test(
            "organization-name",
            PRINTABLE_ASCII_MESSAGE,
            testPrintableAsciiCharacters
          )
          .test(
            "organization-name",
            ALPHANUMERIC_SPACES_MESSAGE,
            testAlphanumericSpaces
          )
          .min(2)
          .max(50),
      otherwise: (s) =>
        s
          .test(
            "organization-name",
            PRINTABLE_ASCII_MESSAGE,
            testPrintableAsciiCharacters
          )
          .test(
            "organization-name",
            ALPHANUMERIC_SPACES_MESSAGE,
            testAlphanumericSpaces
          ),
    })
    .label("Organization Name"),
});

// The userDocuments slice carries docType/docUrl at runtime (legacy redux
// shape); the typed Document model exposes type/uri instead. This component
// reads the legacy keys, so type the prop to the shape it actually consumes.
interface DisclosureDocument {
  docType?: string;
  docUrl?: string;
}

// Mirror of the disclosure object held in state and fed to Formik. The
// constructor seeds string-typed text fields; the async getIraAccountForm
// path resets stockSymbols/familyNames to arrays, hence the union members.
// Boolean/string fields are non-null (matching the form contract); the
// nullable GraphQL values are cast verbatim at the async boundary.
interface DisclosureState {
  noneApply: boolean;
  tradeAuthorization: boolean;
  controlPerson: boolean;
  stockSymbols: string | string[];
  exchangeOrFINRA: boolean;
  firmName: string;
  politicallyExposed: boolean;
  organizationName: string;
  familyNames: string[];
  isCustodianEmployee: boolean;
}

// Shape Formik hands back to submitDisclosure (initialValues forces
// stockSymbols to "").
interface DisclosureFormValues extends DisclosureState {
  stockSymbols: string;
}

const actions = [
  userConstants.USER_ON_BOARDING_DISCLOSURE_UPDATE,
  userConstants.ADD_ROLLOVER_ACCOUNT_DISCLOSURE,
  accountConstants.GET_ACCOUNTS,
];
const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    userState: (state.user.userState as { state: string }).state,
    error: errorSelector(state),
    // legacy userDocuments slice exposes docType/docUrl (see DisclosureDocument)
    documents: state.userDocuments.documents as unknown as DisclosureDocument[],
    isFetching: loadingSelector(state),
    isRolloverAccount: isRolloverSelector(state),
    isSubsequentAccount: isOnboardingSubsequentAccountSelector(state),
  };
};
const mapDispatchToProps = {
  updateIraAccountDisclosure,
  addRolloverAccountDisclosure,
  getUserState,
  getAllAccounts,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  showCancelRolloverModal: boolean;
  disclosure: DisclosureState;
  pepNames: string[];
  submitting: boolean;
  submitted: boolean;
  loading: boolean;
  documentError: string;
  dimensions: {
    width: number;
    height: number;
  };
}

class IndividualDisclosure extends React.Component<Props, State> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      showCancelRolloverModal: false,
      disclosure: {
        noneApply: true,
        tradeAuthorization: false,
        controlPerson: false,
        stockSymbols: "",
        exchangeOrFINRA: false,
        firmName: "",
        politicallyExposed: false,
        organizationName: "",
        familyNames: [],
        isCustodianEmployee: false,
      },
      pepNames: [""],
      submitting: false,
      submitted: false,
      loading: true,
      documentError: "Error",
      dimensions: {
        width: -1,
        height: -1,
      },
    };
    userService
      .getIraAccountForm(this.props.client as ApolloClient<object>)
      .then(
        (data) => {
          const anyAreTrue = some(
            filter(data.disclosure, isBoolean),
            (value) => value === true
          );
          // Boundary cast: the GetIraAccountForm query types these fields as
          // nullable, but the form contract (and initial state) treats them as
          // string/boolean. Values are assigned verbatim, matching the original
          // untyped behavior.
          let disclosure: DisclosureState = {
            noneApply: !anyAreTrue,
            tradeAuthorization: data.disclosure!.tradeAuthorization as boolean,
            controlPerson: data.disclosure!.controlPerson as boolean,
            stockSymbols: [],
            exchangeOrFINRA: data.disclosure!.exchangeOrFINRA as boolean,
            firmName: data.disclosure!.firmName as string,
            politicallyExposed: data.disclosure!.politicallyExposed as boolean,
            organizationName: data.disclosure!.organizationName as string,
            familyNames: [],
            isCustodianEmployee: data.disclosure!
              .isCustodianEmployee as boolean,
          };

          if (this._isMounted) {
            this.setState({
              submitted: false,
              disclosure,
              loading: false,
            });
          }
        },
        () => {
          if (this._isMounted) {
            this.setState({
              submitted: false,
              loading: false,
            });
          }
        }
      );
  }

  handlePepNameChange =
    (idx: number) => (evt: React.ChangeEvent<HTMLInputElement>) => {
      const newPepNames = this.state.pepNames.map((pepName, sidx) => {
        if (idx !== sidx) return pepName;
        return evt.target.value;
      });

      this.setState({ pepNames: newPepNames });
    };

  handleAddPepName = () => {
    this.setState({
      pepNames: this.state.pepNames.concat([""]),
    });
  };

  handleRemovePepName = (idx: number) => () => {
    var newNames = this.state.pepNames;
    newNames.splice(idx, 1);

    this.setState({
      pepNames: newNames,
    });
  };

  componentDidMount() {
    this._isMounted = true;
    scrollToTop();
    window.analytics.page("Individual Disclosure");
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  validDocuments() {
    return some(this.props.documents, { docType: "AFFILIATED_APPROVAL" });
  }

  submitDisclosure = (values: DisclosureFormValues) => {
    if (values.exchangeOrFINRA && values.isCustodianEmployee) {
      // Not valid to have both!
      return;
    }
    if (
      TypeUtils.booleanValue(values.exchangeOrFINRA) &&
      !this.validDocuments()
    ) {
      if (this._isMounted) {
        this.setState({
          submitting: true,
        });
      }
      return;
    }

    var docObj = find(this.props.documents, { docType: "AFFILIATED_APPROVAL" });
    let url = "";
    if (docObj !== undefined) {
      url = docObj.docUrl!;
    }

    let symbols: string[];
    if (values.stockSymbols.length !== 0) {
      symbols = [values.stockSymbols];
    } else {
      symbols = [];
    }

    let names: string[];
    if (this.state.pepNames.length === 1 && this.state.pepNames[0] === "") {
      names = [];
    } else {
      names = this.state.pepNames;
    }
    let disclosure = {
      tradeAuthorization: false,
      controlPerson: TypeUtils.booleanValue(values.controlPerson),
      stockSymbols: symbols,
      exchangeOrFINRA: TypeUtils.booleanValue(values.exchangeOrFINRA),
      firmName: values.firmName,
      politicallyExposed: TypeUtils.booleanValue(values.politicallyExposed),
      organizationName: values.organizationName,
      familyNames: names,
      docUrl: url,
      isCustodianEmployee: TypeUtils.booleanValue(values.isCustodianEmployee),
    };

    if (this.props.isRolloverAccount || this.props.isSubsequentAccount) {
      void this.props.addRolloverAccountDisclosure(
        this.props.client as ApolloClient<object>,
        disclosure
      );
      window.analytics.track("Submitted Rollover Account Disclosure");
    } else {
      void this.props
        .updateIraAccountDisclosure(
          this.props.client as ApolloClient<object>,
          disclosure
        )
        .then(() => {
          void this.props.getUserState(
            this.props.client as ApolloClient<object>
          );
        });
      window.analytics.track("Submitted Account Disclosure", {
        userState: this.props.userState,
      });
    }

    if (this._isMounted) {
      this.setState({
        submitted: true,
      });
    }
  };

  _onCancel = () => {
    this.setState({
      showCancelRolloverModal: true,
    });
  };

  render() {
    if (this.state.loading) {
      return <div></div>;
    }
    return this.renderForm();
  }

  renderForm = () => {
    const { disclosure } = this.state;

    var articleStyle = {
      paddingBottom: 0,
    };

    const successMessage = "Your file was successfully uploaded!";

    const progPercent = this.props.isRolloverAccount ? "40" : "55";

    return (
      <div>
        <ProgressBar
          isEmployer={false}
          activeStepId={DISCLOSURES.id}
          progressPercent={progPercent}
        />
        <Row>
          <Col md={7}>
            {this.state.showCancelRolloverModal && (
              <CancelRolloverModal
                show={this.state.showCancelRolloverModal}
                onClose={() =>
                  this.setState({
                    showCancelRolloverModal: false,
                  })
                }
                onSuccess={() =>
                  this.setState({
                    showCancelRolloverModal: false,
                  })
                }
              />
            )}
            <Formik
              validateOnChange={false}
              validationSchema={schema}
              onSubmit={(values) => {
                this.submitDisclosure(values);
              }}
              enableReinitialize={true}
              initialValues={{ ...disclosure, stockSymbols: "" }}
            >
              {({
                handleSubmit,
                handleChange,
                handleBlur,
                setFieldValue,
                values,
                touched,
                errors,
              }) => (
                <Form noValidate onSubmit={handleSubmit} className="disclosure">
                  <ScrollToFieldError />
                  <div className="mega-container">
                    <div
                      className="step-container is-active"
                      data-circle-percent="70"
                    >
                      <section className="page-title-wrap">
                        <article className="text-cell">
                          <p className="page-eyebrow">DISCLOSURES</p>
                          <p className="page-title">
                            These are uncommon situations, but we have to ask
                            for industry regulations.
                          </p>
                        </article>
                        <article className="progress-cell">
                          <ul className="circular-progress-wrap">
                            <CircularProgressBar
                              strokeWidth="8"
                              sqSize="75"
                              percentage="70"
                            />
                          </ul>
                        </article>
                      </section>
                      <div id="form-employer-company">
                        <section className="form-sec-2col-disclosure">
                          <article className="col-form" style={articleStyle}>
                            <Form.Check
                              className="mb-3 control-checkbox-noneApply"
                              id="formBasicNoneApply"
                              type="checkbox"
                              name="noneApply"
                              checked={values.noneApply}
                              onChange={(e) => {
                                void setFieldValue(
                                  "noneApply",
                                  e.target.checked
                                );
                                if (e.target.checked) {
                                  void setFieldValue("controlPerson", false);
                                  void setFieldValue("exchangeOrFINRA", false);
                                  void setFieldValue(
                                    "politicallyExposed",
                                    false
                                  );
                                  void setFieldValue(
                                    "isCustodianEmployee",
                                    false
                                  );
                                }

                                window.analytics.track("Clicked None Apply");
                              }}
                              label={
                                <b className="bold">
                                  None of these apply to me.
                                </b>
                              }
                            />

                            <Form.Check
                              className="mb-3 disclosure-control-checkbox"
                              id="formBasicControlPerson"
                              type="checkbox"
                              checked={values.controlPerson}
                              name="controlPerson"
                              // @ts-expect-error inert `value` on a checkbox: the control is driven by `checked`; boolean preserved verbatim
                              value={values.controlPerson}
                              onBlur={handleBlur}
                              onChange={(e) => {
                                void setFieldValue(
                                  "controlPerson",
                                  e.target.checked
                                );
                                void setFieldValue("noneApply", false);
                                window.analytics.track(
                                  "Clicked Control Person"
                                );
                              }}
                              label={
                                <>
                                  <b className="bold">Control Person:</b> Are
                                  you a control person of a publicly traded
                                  company (Director, Officer, or 10% Stock
                                  Owner)?
                                </>
                              }
                            />
                            {values.controlPerson === true ? (
                              <Row
                                className="mb-3 disclosure-context"
                                controlId="formBasicStockSymbols"
                              >
                                <Form.Group
                                  as={Col}
                                  controlId="formBasicStockSymbols"
                                >
                                  <Form.Label>
                                    Please list the stock ticker symbol:
                                  </Form.Label>
                                  <Form.Control
                                    name="stockSymbols"
                                    placeholder="Stock symbol"
                                    className="stock-symbol"
                                    value={values.stockSymbols}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={
                                      touched.stockSymbols &&
                                      !!errors.stockSymbols
                                    }
                                    isValid={
                                      touched.stockSymbols &&
                                      !errors.stockSymbols
                                    }
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {errors.stockSymbols}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Row>
                            ) : (
                              <div></div>
                            )}
                            <Form.Check
                              className="mb-3 disclosure-control-checkbox"
                              id="formBasicExchangeOrFINRA"
                              type="checkbox"
                              name="exchangeOrFINRA"
                              checked={values.exchangeOrFINRA}
                              // @ts-expect-error inert `value` on a checkbox: the control is driven by `checked`; boolean preserved verbatim
                              value={values.exchangeOrFINRA}
                              onBlur={handleBlur}
                              onChange={(e) => {
                                void setFieldValue(
                                  "exchangeOrFINRA",
                                  e.target.checked
                                );
                                void setFieldValue("noneApply", false);
                                window.analytics.track(
                                  "Clicked Exchange Affiliation"
                                );
                              }}
                              label={
                                <>
                                  <b className="bold">Exchange Affiliation:</b>{" "}
                                  Are you affiliated with, work with, or work
                                  for a member firm of a Stock Exchange or
                                  FINRA?
                                </>
                              }
                            />
                            {values.exchangeOrFINRA === true ? (
                              <React.Fragment>
                                <Row className="mb-3 disclosure-context">
                                  <Form.Group
                                    as={Col}
                                    controlId="formBasicTradeFirm"
                                  >
                                    <Form.Label>
                                      Please enter the name of the firm:
                                    </Form.Label>
                                    <Form.Control
                                      name="firmName"
                                      placeholder="Firm name"
                                      value={values.firmName}
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      isInvalid={
                                        touched.firmName && !!errors.firmName
                                      }
                                      isValid={
                                        touched.firmName && !errors.firmName
                                      }
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {errors.firmName}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Row>
                                <Row className="mb-3 disclosure-context">
                                  <Form.Group
                                    as={Col}
                                    controlId="formBasicTradeFormUpload"
                                  >
                                    <Form.Label>
                                      Please upload an image documenting written
                                      consent of the member firm for opening
                                      your account, pursuant to FINRA
                                      incorporated NYSE Rule 3210(407):
                                    </Form.Label>
                                  </Form.Group>
                                </Row>
                                <Row className="mb-3">
                                  <Form.Group
                                    as={Col}
                                    controlId="formBasicTradeFormUploadField"
                                  >
                                    <FileUploader
                                      fileLimit={1}
                                      docType={"AFFILIATED_APPROVAL"}
                                      persist={true}
                                      fileUploadedMsg={successMessage}
                                    />
                                  </Form.Group>
                                </Row>
                              </React.Fragment>
                            ) : (
                              <div></div>
                            )}
                            <Form.Check
                              className="mb-3 disclosure-control-checkbox"
                              id="formBasicPoliticallyExposed"
                              type="checkbox"
                              name="politicallyExposed"
                              checked={values.politicallyExposed}
                              // @ts-expect-error inert `value` on a checkbox: the control is driven by `checked`; boolean preserved verbatim
                              value={values.politicallyExposed}
                              onBlur={handleBlur}
                              onChange={(e) => {
                                void setFieldValue(
                                  "politicallyExposed",
                                  e.target.checked
                                );
                                void setFieldValue("noneApply", false);
                                window.analytics.track(
                                  "Clicked Politically Exposed"
                                );
                              }}
                              label={
                                <>
                                  <b className="bold">Politically Exposed:</b>{" "}
                                  Will this account be maintained for a current
                                  or former Politically Exposed Person or Public
                                  Official (includes U.S. and Foreign
                                  Individuals)?
                                </>
                              }
                            />
                            {values.politicallyExposed === true ? (
                              <React.Fragment>
                                <Row className="mb-3 disclosure-context">
                                  <Form.Group
                                    as={Col}
                                    controlId="formBasicPepOrganization"
                                  >
                                    <Form.Label>
                                      Please provide the name of the related
                                      political organization:{" "}
                                    </Form.Label>
                                    <Form.Control
                                      name="organizationName"
                                      placeholder="Organization Name"
                                      value={values.organizationName}
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      isInvalid={
                                        touched.organizationName &&
                                        !!errors.organizationName
                                      }
                                      isValid={
                                        touched.organizationName &&
                                        !errors.organizationName
                                      }
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {errors.organizationName}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Row>
                                <Row className="mb-3 disclosure-context">
                                  <Form.Group
                                    as={Col}
                                    sm={12}
                                    controlId="formBasicPoliticallyExposedNames"
                                  >
                                    <Form.Label>
                                      Please provide the names of that person
                                      {"'"}s or official{"'"}s immediate family
                                      members (including former spouses):
                                    </Form.Label>
                                  </Form.Group>
                                </Row>
                                {this.state.pepNames.map((pepName, idx) => (
                                  <Row className="mb-3 family-member" key={idx}>
                                    <Form.Group
                                      as={Col}
                                      sm={8}
                                      controlId="formBasicPoliticallyExposedNames"
                                    >
                                      <Form.Control
                                        type="text"
                                        name={`pepName.${idx}`}
                                        placeholder={`Family member #${
                                          idx + 1
                                        } Full Legal Name`}
                                        value={pepName}
                                        onChange={this.handlePepNameChange(idx)}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        {/* PRESERVED BUG (conversion-only): `pepName` is not a Formik
                                            field (pepNames lives in component state), so errors.pepName
                                            is always undefined — a latent no-op preserved verbatim. */}
                                        {/* @ts-expect-error pepName is not a key of the Formik values */}
                                        {errors.pepName}
                                      </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group
                                      as={Col}
                                      sm={2}
                                      controlId="formBasicPoliticallyExposedButton"
                                    >
                                      {this.state.pepNames.length > 1 ? (
                                        <div className="minus-btn">
                                          <FiMinus
                                            type="button"
                                            size={"24px"}
                                            color={"#333333"}
                                            fill={"#333333"}
                                            stroke={"#333333"}
                                            strokeWidth={"1.5"}
                                            onClick={this.handleRemovePepName(
                                              idx
                                            )}
                                          />
                                        </div>
                                      ) : (
                                        <div></div>
                                      )}
                                    </Form.Group>
                                  </Row>
                                ))}
                                {this.state.pepNames.length <= 9 ? (
                                  <div className="add-member-btn">
                                    <FiPlus
                                      type="button"
                                      onClick={this.handleAddPepName}
                                      size={"24px"}
                                      color={"#1D99A9"}
                                      fill={"#1D99A9"}
                                      stroke={"#1D99A9"}
                                      strokeWidth={"1.5"}
                                    />
                                  </div>
                                ) : (
                                  <div></div>
                                )}
                              </React.Fragment>
                            ) : (
                              <div></div>
                            )}
                            <Form.Check
                              className="mb-3 disclosure-control-checkbox"
                              id="formCustodianEmployee"
                              type="checkbox"
                              name="isCustodianEmployee"
                              checked={values.isCustodianEmployee}
                              // @ts-expect-error inert `value` on a checkbox: the control is driven by `checked`; boolean preserved verbatim
                              value={values.isCustodianEmployee}
                              onBlur={handleBlur}
                              onChange={(e) => {
                                void setFieldValue(
                                  "isCustodianEmployee",
                                  e.target.checked
                                );
                                void setFieldValue("noneApply", false);
                                window.analytics.track(
                                  "Clicked Custodian Employee"
                                );
                              }}
                              label={
                                <>
                                  <b className="bold">Custodian Employee:</b>{" "}
                                  Are you an employee of, or affiliated with
                                  Apex Fintech Solutions?
                                </>
                              }
                            />
                          </article>
                        </section>
                        <section className="form-sec-2col">
                          <article className="col-form">
                            <div className="disclosure-submit-row">
                              {this.props.error && (
                                <Alert type="error" msg={this.props.error} />
                              )}
                              {!this.validDocuments() &&
                              values.exchangeOrFINRA === true &&
                              this.state.submitting ? (
                                <Alert
                                  type="error"
                                  msg="Document upload required."
                                />
                              ) : null}
                              {values.isCustodianEmployee && (
                                <Alert
                                  type="warning"
                                  msg="You have selected that you are an employee of, or affiliated with Apex Fintech Solutions, this is not a common scenario. Please ensure you have selected the correct option."
                                />
                              )}
                              {values.isCustodianEmployee &&
                              values.exchangeOrFINRA ? (
                                <Alert
                                  type="error"
                                  msg="You cannot be a custodian employee and affiliated with a member firm of a Stock Exchange or FINRA."
                                />
                              ) : null}
                              <div className="submit-row btn-row">
                                {this.props.isRolloverAccount && (
                                  <Button
                                    type="button"
                                    btnLabel={"Cancel"}
                                    color={"cancel"}
                                    name="cancel"
                                    onClick={this._onCancel}
                                  />
                                )}
                                <Button
                                  name="submit"
                                  withArrow={true}
                                  loading={this.props.isFetching}
                                />
                              </div>
                            </div>
                          </article>
                        </section>
                      </div>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </div>
    );
  };
}

export default connector(withApollo<Props>(IndividualDisclosure));
